import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useAuth0Login } from "~/hooks/useAuth0Login";
import { useAuthenticatedUser } from "~/hooks/useAuthenticatedUser";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "~/lib/orpc-client";
import { Spinner } from "~/components/ui/spinner";
import { useState } from "react";
import {
  Users,
  Mail,
  UserPlus,
  Trash2,
  Check,
  Shield,
  Crown,
  AlertCircle,
} from "lucide-react";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import UniversityInviteModal from "~/components/university/university-invite-modal";
import UniversityRemoveAccessModal from "~/components/university/university-remove-access-modal";
import { QUERY_KEYS } from "~/constants/queryKeys";

export const Route = createFileRoute(
  "/$tenantSlug/_protected/access-management",
)({
  component: AccessManagementPage,
});

function AccessManagementPage() {
  const { isAuthenticated, isLoading, user } = useAuth0Login();
  const {
    capabilities,
    routing,
    isLoading: isUserLoading,
  } = useAuthenticatedUser();
  const queryClient = useQueryClient();

  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<{
    userId: string;
    name: string;
    email?: string;
  } | null>(null);
  const [invitationToRevoke, setInvitationToRevoke] = useState<{
    id: string;
    email: string;
  } | null>(null);
  const [resendingEmail, setResendingEmail] = useState<string | null>(null);
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);

  const { data: orgData, isLoading: isLoadingOrg } = useQuery({
    queryKey: [QUERY_KEYS.MY_ORGANIZATION],
    queryFn: () => apiClient.organizations.getMyOrganization(),
    enabled: isAuthenticated,
  });

  const orgId = orgData?.organization?.id;

  const {
    data: membersData,
    isLoading: isLoadingMembers,
  } = useQuery({
    queryKey: QUERY_KEYS.UNIVERSITY_ACCESS_MEMBERS(orgId || ""),
    queryFn: () => apiClient.universityAccess.listMembers({ orgId: orgId! }),
    enabled: !!orgId,
  });

  const {
    data: invitationsData,
  } = useQuery({
    queryKey: QUERY_KEYS.UNIVERSITY_ACCESS_INVITATIONS(orgId || ""),
    queryFn: () =>
      apiClient.universityAccess.listInvitations({ orgId: orgId! }),
    enabled: !!orgId,
  });

  const invalidateAccessData = () => {
    if (!orgId) return;
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.UNIVERSITY_ACCESS_MEMBERS(orgId) });
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.UNIVERSITY_ACCESS_INVITATIONS(orgId) });
  };

  const showSuccess = (message: string) => {
    setErrorMessage(null);
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  type MembersData = typeof membersData;

  const updateRoleMutation = useMutation({
    mutationFn: async ({
      userId,
      role,
    }: {
      userId: string;
      role: "UNIVERSITY_ADMIN" | "UNIVERSITY_OWNER";
    }) =>
      apiClient.universityAccess.updateMemberRole({
        orgId: orgId!,
        userId,
        role,
      }),
    onMutate: async ({ userId, role }) => {
      setUpdatingUserId(userId);

      // Cancel outgoing refetches so they don't overwrite our optimistic update
      const queryKey = QUERY_KEYS.UNIVERSITY_ACCESS_MEMBERS(orgId || "");
      await queryClient.cancelQueries({ queryKey });

      // Snapshot current data for rollback
      const previous = queryClient.getQueryData<MembersData>(queryKey);

      // Optimistically update the member's role in the cache
      queryClient.setQueryData<MembersData>(queryKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          members: old.members.map((m) =>
            m.userId === userId ? { ...m, universityRole: role } : m,
          ),
        };
      });

      return { previous };
    },
    onSuccess: (data) => {
      // Background revalidate to ensure server state is in sync
      invalidateAccessData();
      showSuccess(data.message);
    },
    onError: (error: Error, _vars, context) => {
      // Roll back the optimistic update
      if (context?.previous) {
        const queryKey = QUERY_KEYS.UNIVERSITY_ACCESS_MEMBERS(orgId || "");
        queryClient.setQueryData(queryKey, context.previous);
      }
      setSuccessMessage(null);
      setErrorMessage(error.message || "Failed to update role");
      setTimeout(() => setErrorMessage(null), 8000);
    },
    onSettled: () => {
      setUpdatingUserId(null);
    },
  });

  const resendInvitationMutation = useMutation({
    mutationFn: ({ invitationId }: { invitationId: string; email: string }) =>
      apiClient.universityAccess.resendInvitation({
        orgId: orgId!,
        invitationId,
      }),
    onMutate: ({ email }) => setResendingEmail(email),
    onSuccess: () => {
      invalidateAccessData();
      showSuccess("Invitation resent successfully");
    },
    onSettled: () => setResendingEmail(null),
  });

  const revokeInvitationMutation = useMutation({
    mutationFn: (invitationId: string) =>
      apiClient.universityAccess.deleteInvitation({
        orgId: orgId!,
        invitationId,
      }),
    onSuccess: () => {
      invalidateAccessData();
      setInvitationToRevoke(null);
      showSuccess("Invitation revoked");
    },
  });

  const currentUserAuth0Id = user?.sub;

  if (isLoading || isUserLoading || isLoadingOrg) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <Spinner className="size-8 text-brand-green-primary" />
      </div>
    );
  }

  if (!capabilities?.canManageUniversityAccess) {
    return (
      <Navigate to={(routing?.homePath ?? "/dashboard")} replace />
    );
  }

  const org = orgData?.organization;
  if (!org) return <div>No organization found</div>;

  const members = membersData?.members || [];
  const phStaffMembers = membersData?.phStaffMembers || [];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-foreground">
            Access Management
          </h1>
          <p className="text-sm text-foreground/70 mt-1">
            Manage who has administrative access to {org.displayName || org.name}
          </p>
        </div>

        {successMessage && (
          <div className="mb-6 flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm max-w-xl">
            <Check className="w-4 h-4" />
            {successMessage}
          </div>
        )}

        {errorMessage && (
          <div className="mb-6 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm max-w-xl">
            <AlertCircle className="w-4 h-4" />
            {errorMessage}
          </div>
        )}

        <div className="grid gap-6 max-w-xl">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Team ({members.length})
                  </CardTitle>
                  <CardDescription>
                    People with admin or owner access
                  </CardDescription>
                </div>
                <Button size="sm" onClick={() => setShowInviteModal(true)}>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add User
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingMembers ? (
                <div className="flex justify-center py-8">
                  <Spinner className="w-6 h-6 text-brand-green-primary" />
                </div>
              ) : members.length === 0 ? (
                <div className="text-center py-8 text-foreground/70">
                  <Users className="w-12 h-12 mx-auto mb-3 text-foreground/30" />
                  <p>No elevated access users yet</p>
                  <p className="text-sm">
                    Add users to grant admin or owner access
                  </p>
                </div>
              ) : (
                <div className="divide-y">
                  {members.map((member) => {
                    const isOwner =
                      member.universityRole === "UNIVERSITY_OWNER";
                    const isSelf = member.userId === currentUserAuth0Id;
                    return (
                      <div
                        key={member.userId}
                        className="flex items-center justify-between py-4 first:pt-0 last:pb-0"
                      >
                        <div className="flex items-center gap-3">
                          {member.picture ? (
                            <img
                              src={member.picture}
                              alt={member.name || member.email || "Member"}
                              className="w-10 h-10 rounded-full"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-brand-green-light/30 flex items-center justify-center">
                              <span className="text-brand-green-primary font-medium">
                                {(
                                  member.name ||
                                  member.email ||
                                  "?"
                                )?.[0]?.toUpperCase() || "?"}
                              </span>
                            </div>
                          )}
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-foreground">
                                {member.name || "Unknown"}
                                {isSelf && (
                                  <span className="ml-2 text-xs text-black font-normal">
                                    (Me)
                                  </span>
                                )}
                              </p>
                            </div>
                            {member.email && (
                              <p className="text-xs text-foreground/60">
                                {member.email}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {!isSelf ? (
                            updatingUserId === member.userId ? (
                              <div className="flex items-center gap-1.5 px-2 py-0.5">
                                <Spinner className="w-3.5 h-3.5 text-foreground/40" />
                                <span className="text-xs text-foreground/50">Saving...</span>
                              </div>
                            ) : (
                              <select
                                className="text-xs border rounded-lg px-2 py-1 bg-white"
                                value={
                                  member.universityRole || "UNIVERSITY_ADMIN"
                                }
                                onChange={(e) =>
                                  updateRoleMutation.mutate({
                                    userId: member.userId,
                                    role: e.target.value as
                                      | "UNIVERSITY_ADMIN"
                                      | "UNIVERSITY_OWNER",
                                  })
                                }
                              >
                                <option value="UNIVERSITY_OWNER">Owner</option>
                                <option value="UNIVERSITY_ADMIN">Admin</option>
                              </select>
                            )
                          ) : isOwner ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                              <Crown className="w-3 h-3" />
                              Owner
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              <Shield className="w-3 h-3" />
                              Admin
                            </span>
                          )}
                          {!isSelf && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                setMemberToRemove({
                                  userId: member.userId,
                                  name: member.name || member.email || "Unknown",
                                  email: member.email,
                                })
                              }
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {invitationsData?.invitations &&
                invitationsData.invitations.length > 0 && (
                  <div className="mt-6 pt-6 border-t">
                    <h4 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Pending Invitations (
                      {invitationsData.invitations.length})
                    </h4>
                    <div className="space-y-3">
                      {invitationsData.invitations.map((invitation) => (
                        <div
                          key={invitation.id}
                          className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg"
                        >
                          <div>
                            <p className="font-medium text-foreground">
                              {invitation.email}
                            </p>
                            <p className="text-xs text-foreground/70">
                              {invitation.roleName && (
                                <span className="mr-2">
                                  Role: {invitation.roleName}
                                </span>
                              )}
                              Expires{" "}
                              {new Date(
                                invitation.expiresAt,
                              ).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                resendInvitationMutation.mutate({
                                  invitationId: invitation.id,
                                  email: invitation.email,
                                })
                              }
                              disabled={resendInvitationMutation.isPending}
                            >
                              {resendInvitationMutation.isPending &&
                                resendingEmail === invitation.email
                                ? "Resending..."
                                : "Resend"}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setInvitationToRevoke({
                                  id: invitation.id,
                                  email: invitation.email,
                                })
                              }
                              className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                            >
                              Revoke
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </CardContent>
          </Card>

          {phStaffMembers.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Proudly Human Access ({phStaffMembers.length})
                </CardTitle>
                <CardDescription>
                  Proudly Human team members have access in order to support the Student Authenticity Portal university administrator.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="divide-y">
                  {phStaffMembers.map((member) => (
                    <div
                      key={member.userId}
                      className="flex items-center justify-between py-4 first:pt-0 last:pb-0"
                    >
                      <div className="flex items-center gap-3">
                        {member.picture ? (
                          <img
                            src={member.picture}
                            alt={member.name || member.email || "Member"}
                            className="w-10 h-10 rounded-full"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <span className="text-blue-700 font-medium">
                              {(
                                member.name ||
                                member.email ||
                                "?"
                              )?.[0]?.toUpperCase() || "?"}
                            </span>
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-foreground">
                            {member.name || "Unknown"}
                            {member.userId === currentUserAuth0Id && (
                              <span className="ml-2 text-xs text-black font-normal">
                                (Me)
                              </span>
                            )}
                          </p>
                          {member.email && (
                            <p className="text-xs text-foreground/60">
                              {member.email}
                            </p>
                          )}
                        </div>
                      </div>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        <Shield className="w-3 h-3" />
                        PH Staff
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <UniversityInviteModal
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        orgId={org.id}
        orgDisplayName={org.displayName || org.name || ""}
        userEmail={user?.email}
        onSuccess={(message) => {
          showSuccess(message);
          invalidateAccessData();
        }}
      />

      <UniversityRemoveAccessModal
        open={!!memberToRemove}
        onClose={() => setMemberToRemove(null)}
        orgId={org.id}
        member={memberToRemove}
        onSuccess={() => {
          invalidateAccessData();
          showSuccess("Access removed successfully");
        }}
      />

      <Dialog
        open={!!invitationToRevoke}
        onOpenChange={(isOpen) => { if (!isOpen) setInvitationToRevoke(null); }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke Invitation</DialogTitle>
            <DialogDescription>
              Cancel the pending invitation to {invitationToRevoke?.email}
            </DialogDescription>
          </DialogHeader>

          <p className="text-sm text-foreground">
            They will no longer be able to join using this invitation link.
          </p>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setInvitationToRevoke(null)}
              disabled={revokeInvitationMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (invitationToRevoke) {
                  revokeInvitationMutation.mutate(invitationToRevoke.id);
                }
              }}
              disabled={revokeInvitationMutation.isPending}
            >
              {revokeInvitationMutation.isPending
                ? "Revoking..."
                : "Revoke Invitation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
